import React from 'react';
import {FormControl, MenuItem, Select, Stack, TextField} from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import {DescriptorFormField} from './DescriptorFormField.component';
import {DescriptorFormState, ValidationState} from '../descriptor-types';
import {DescriptorState, ERROR_MESSAGES} from '../descriptor-constants';
import Group from '../../../../../../@types/group';
import {ResourceType} from '../../../../../../@types/resource-type';
import {getResourceNameWithAscendants} from '../../../../../../utils/resource-utils';
import OccurrenceReportDescriptor from '../../../../../../@types/occurrence-report-descriptor';

interface DescriptorBasicInfoProps {
    formState: DescriptorFormState;
    validation: ValidationState;
    groups: Group[];
    descriptor: OccurrenceReportDescriptor;
    componentId: string;
    isNotEditable: boolean;
    onNameChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onDescriptionChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onUriChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onUriBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onGroupChange: (event: any) => void;
}

/**
 * Component for basic descriptor information fields
 */
export const DescriptorBasicInfo: React.FC<DescriptorBasicInfoProps> = ({
    formState,
    validation,
    groups,
    descriptor,
    componentId,
    isNotEditable,
    onNameChange,
    onDescriptionChange,
    onUriChange,
    onUriBlur,
    onGroupChange
}) => {
    return (<Stack spacing={2} direction="column" justifyContent="space-evenly">
        <Stack direction="row" spacing={2} sx={{mb: 2}}>
            <DescriptorFormField
                label="Name"
                value={formState.name}
                onChange={onNameChange}
                disabled={isNotEditable}
                required
                error={validation.nameError}
                helperText={validation.nameError ? ERROR_MESSAGES.NAME_REQUIRED : ''}
                componentId={componentId}
            />
            <TextField
                label="State"
                aria-label="state"
                type="text"
                variant="outlined"
                size="small"
                id={`state-input-${componentId}`}
                autoComplete="on"
                value={formState.status || ''}
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                slotProps={{
                    input: {
                        'role': 'textbox',
                        'tabIndex': 0,
                        'aria-labelledby': `state-input-${componentId}`,
                        'aria-label': 'state'
                    }
                }}
                disabled
                fullWidth
            />
        </Stack>

        <Stack direction="row" spacing={2} sx={{mb: 2}}>
            <DescriptorFormField
                label="URI"
                value={formState.uri}
                onChange={onUriChange}
                onBlur={onUriBlur}
                disabled={isNotEditable}
                error={validation.uriError}
                helperText={validation.uriError ? ERROR_MESSAGES.URI_INVALID : ''}
                componentId={componentId}
            />
            {descriptor.id !== undefined ? <TextField
                label="Working Area"
                aria-label="working area"
                type="text"
                variant="outlined"
                size="small"
                id={`group-input-${componentId}`}
                value={descriptor.descriptorState === DescriptorState.DRAFT ? getResourceNameWithAscendants(descriptor.group, ResourceType.GROUPS) : 'UNDEFINED'}
                sx={{
                    '& input': {
                        border: 'none',
                    }
                }}
                slotProps={{
                    input: {
                        'role': 'textbox',
                        'tabIndex': 0,
                        'aria-labelledby': `group-input-${componentId}`,
                        'aria-label': 'working area'
                    }
                }}
                fullWidth
                disabled
            /> : <FormControl fullWidth>
                <InputLabel
                    tabIndex={-1}
                    id={`select-label-${componentId}`}
                    htmlFor={`select-list-${componentId}`}
                    size={'small'}
                >
                    Working Area
                </InputLabel>
                <Select
                    role="presentation"
                    labelId={`select-label-${componentId}`}
                    id={`select-${componentId}`}
                    value={formState.groupId || ''}
                    label="Working Area"
                    size="small"
                    fullWidth={true}
                    onChange={onGroupChange}
                    inputProps={{
                        'role': 'textbox', 'tabIndex': 0, 'id': `select-list-${componentId}`,
                    }}
                    disabled={isNotEditable}
                >
                    {(isNotEditable ? groups.map((group) => (<MenuItem key={group.id} value={group.id} dense>
                        {getResourceNameWithAscendants(group, ResourceType.GROUPS)}
                    </MenuItem>)) : groups.filter(group => group.canAddOrEditResources()).map((group) => (
                        <MenuItem key={group.id} value={group.id} dense>
                            {getResourceNameWithAscendants(group, ResourceType.GROUPS)}
                        </MenuItem>)))}
                </Select>
            </FormControl>}
        </Stack>

        <DescriptorFormField
            label="Description"
            value={formState.description}
            onChange={onDescriptionChange}
            disabled={isNotEditable}
            required
            multiline
            rows={2}
            error={validation.descriptionError}
            helperText={validation.descriptionError ? ERROR_MESSAGES.DESCRIPTION_REQUIRED : ''}
            componentId={componentId}
        />
    </Stack>);
};